
import AppEditPurchase from "@/components/purchases/PurchaseEdit.vue";
import { Contact, Purchase } from "@/models/base";
import { PurchaseService } from "@/services/purchase-service";
import { PendingChangesNotifier } from "@/utils/pending-changes-notifier";
import Vue from "vue";
import { checkHasModule } from "../../router";
import { MODULE } from "../../config/modules";
import { ToolbarService } from "@/services/toolbar-service";
import { ConfirmResult } from "winfakt-vue-components";
import { AuthService, AWSFileService, BookYearService, DossierConfigService, ModalService } from "@/services";
import {TranslateResult} from "vue-i18n";
import { InboxreaderPurchase } from "@/utils/purchase/inboxreader-purchase";
import { IUblPurchaseWrapper } from "@/plugins/ubl/services/ubl-service";
import { base64ToBlob } from "@/utils/blob";
import { InboxreaderService } from "@/services/inboxreader-service";

export default Vue.extend({
	data() {
		let warningText = () => {
			return this.$ct("save-confirm").toString();
		};
		return {
			purchase: new Purchase(),
			pendingChangesNotifier: new PendingChangesNotifier(this, "purchase", {warningText}),
			ready: false,
			saveDisabled: false,
		};
	},
	props: {
		purchaseFromInbox: {
			type: Object as () => Purchase,
			required: false
		}
	},
	computed: {
		title():TranslateResult{
			return ToolbarService.title;
		},
	},
	methods: {
		goToBrowse() {
			this.$emit("update");
			this.pendingChangesNotifier.changesPending = false;
			if(this.purchaseFromInbox){
				this.$emit("close");
				return;
			}
			this.$router.push("/purchase");
		},
		async save():Promise<boolean>{
			if (!this.$refs.root) return false;
			return (this.$refs.root as any).save();
		},
		async fetchInboxreaderPurchaseAttachments(p:InboxreaderPurchase){
			try{
				this.saveDisabled = true;
				for (let file of p.Files){
					if (!file.File) continue;
					let f = await AWSFileService.fetchFile(file.File.Token, file.File.Name, file.File.ContentType);
					this.purchase.UnprocessedFiles.push(f);
				}
			}catch(err){

			}finally{
				this.saveDisabled = false;
			}
		},
		async askToCreateContact(contact:Contact, isUbl = false){
			let name = contact.getDisplayName();
			let tag = "peppol-contact-does-not-exist-do-you-want-to-create-it";
			if (isUbl){
				tag = "ubl-contact-does-not-exist-do-you-want-to-create-it";
			}
			let message = this.$ct(tag, {name});
			let confirm = await this.$confirmBuilder(message).show();
			if (!confirm) {
				this.purchase.Contact = null;
				this.purchase.ContactID = 0;
				return;
			}
			let result = await ModalService.openEditContactModal(2, contact);
			if (!result || result.ID == 0){
				this.purchase.Contact = null;
				this.purchase.ContactID = 0;
				return;
			}
			this.purchase.Contact = result;
			this.purchase.ContactID = result.ID;
		},
		async fetchFromInboxReader(inboxreaderId:number){
			let p = await InboxreaderService.getInboxreaderPurchase(inboxreaderId);
			if (p.PeppolOrder) {
				this.purchase = await InboxreaderService.getInboxreaderPurchaseWithPeppolOrder(inboxreaderId);
				if (this.purchase.Contact && this.purchase.Contact.ID == 0){
					await this.askToCreateContact(this.purchase.Contact);
				}else{
					let possibleDuplicate = await PurchaseService.findPossibleDuplicatePurchase(this.purchase);
					if (possibleDuplicate) {
						let message = this.$ct("this-document-is-a-possible-duplicate-of-number", {
							documentId: possibleDuplicate.ID,
							documentNr: possibleDuplicate.ComputedFriendlyID
						});
						await this.$wf.dialog(message.toString(), this.$ct("common.ok"), this.$ct("common.warning"), "warning");
					}
				}
			}
			this.purchase.InboxreaderPurchaseID = p.ID;
			this.purchase.InboxreaderMessageID = p.MessageID;
			this.fetchInboxreaderPurchaseAttachments(p);

		},
		async fetchFromSelfBilledPurchase(purchaseId:number){
			this.purchase = await InboxreaderService.purchaseFromSelfBilledSale(purchaseId);
			let possibleDuplicate = await PurchaseService.findPossibleDuplicatePurchase(this.purchase);
			if (possibleDuplicate) {
				let message = this.$ct("this-document-is-a-possible-duplicate-of-number", {
					documentId: possibleDuplicate.ID,
					documentNr: possibleDuplicate.ComputedFriendlyID
				});
				await this.$wf.dialog(message.toString(), this.$ct("common.ok"), this.$ct("common.warning"), "warning");
			}
		},
	},
	async created() {
		if (!checkHasModule(MODULE.AANKOOPFACTUREN, this.$router)){
			throw new Error("MISSING MODULE");
		}
	},
	// eslint-disable-next-line max-lines-per-function
	async mounted() {
		ToolbarService.title =  this.$route.params.id ? this.$ct("components.purchases.edit-purchase") : this.$ct("components.purchases.new-purchase");
		this.purchase.BookYear = BookYearService.selectedBookYear;
		let id = parseInt(this.$route.params.id || "0");
		let inboxreaderId = parseInt(this.$route.params.inboxreaderId || "0");
		let selfBilledPurchaseId = parseInt(this.$route.params.selfBilledPurchaseId || "0");
		let recomputeVats = true;
		if (id != 0) {
			this.purchase = await PurchaseService.getPurchase(id);
			if (this.purchase.ComputedFriendlyID.startsWith("-")){
				this.$wf.notify(this.$ct("components.browse.saldo-warning"), "danger", 3000, "exclamation-triangle");
				this.$router.push("/purchase");
			}
			recomputeVats = false;
		}else if (this.$route.query.fromJson){
			let wrapper:IUblPurchaseWrapper = JSON.parse(this.$route.query.fromJson as string);
			this.purchase = new Purchase(wrapper.Purchase);
			this.purchase.ID = 0;
			if (this.purchase.Contact && this.purchase.Contact.ID == 0 && this.purchase.ContactID == 0){
				await this.askToCreateContact(this.purchase.Contact, true);
			}
			for (let attachment of wrapper.Attachments) {
				let blob = base64ToBlob(attachment.FileContent, attachment.MimeType);
				let file = new File([blob], attachment.FileName);
				this.purchase.UnprocessedFiles.push(file);
			}
		}else if (inboxreaderId != 0) {
			await this.fetchFromInboxReader(inboxreaderId);
		}else if (selfBilledPurchaseId != 0){
			if (!AuthService.hasModule(MODULE.SELF_BILLING)){
				return this.$router.push("/missing-module/" + MODULE.SELF_BILLING);
			}
			await this.fetchFromSelfBilledPurchase(selfBilledPurchaseId);
		}
		this.pendingChangesNotifier.changesPending = false;
		this.ready = true;
		if (this.purchase.ID == 0 && this.purchase.ExtraFields){
			DossierConfigService.getExtraFieldNamesConfigs().PurchaseNames.setDefaultValues(this.purchase.ExtraFields);
		}
		if(this.purchaseFromInbox){
			this.purchase = new Purchase(this.purchaseFromInbox);
			recomputeVats = true;
		}
		if (recomputeVats) {
			await this.$nextTick();
			(this.$refs.root as any).checkAndFillVatsByLedger();
		}
	},
	components: {
		AppEditPurchase
	},
	i18nEx: {
		componentPrefix: "components.purchases"
	},
	async beforeRouteLeave(to, from, next) {
		let result = await this.pendingChangesNotifier.askToSave();
		if (result == ConfirmResult.CANCEL){
			return;
		}
		if (result == ConfirmResult.YES) {
			let result = await this.save();
			if (!result) {
				return;
			}
		}
		next();
	}
});
